import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Shield, Heart, Sparkles, Star } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API, resolveImageUrl, useAuth } from "../App";
import { toast } from "sonner";

// ── Static data ──────────────────────────────────────────────────────────────

const CATEGORIES = [
  { name: "Rings",          image: "/Rings.png" },
  { name: "Bracelets",      image: "/Braclet.png" },
  { name: "Pendant Chains", image: "/Pendant Chains.png" },
  { name: "Earrings",       image: "/Earrings.png" },
];

const TRUST_POINTS = [
  { icon: Shield,   label: "92.5 Silver Certified",     desc: "Hallmarked sterling silver for lasting shine and everyday elegance." },
  { icon: Heart,    label: "Skin-friendly & safe",       desc: "Hypoallergenic and safe for sensitive skin." },
  { icon: Sparkles, label: "Handcrafted in Jaipur",      desc: "Made by skilled artisans in India's jewellery capital." },
  { icon: Star,     label: "Everyday Luxury",            desc: "Lightweight designs made to be worn every day." }
];

const LIFESTYLE_IMAGES = [
  { src: "/ChatGPT Image Jul 1, 2026, 05_42_35 PM.png", alt: "Rings lifestyle" },
  { src: "/ChatGPT Image Jul 1, 2026, 05_42_50 PM.png", alt: "Bracelet lifestyle" },
  { src: "/ChatGPT Image Jul 1, 2026, 05_42_57 PM.png", alt: "Pendant lifestyle" },
  { src: "/ChatGPT Image Jul 1, 2026, 05_47_07 PM.png", alt: "Earrings lifestyle" },
];

const HOMEPAGE_REVIEWS = [
  { id: 1, name: "Priya S.",   location: "Mumbai",    rating: 5, text: "Absolutely love my silver ring! The quality is outstanding and they look exactly as pictured." },
  { id: 2, name: "Ananya R.",  location: "Delhi",     rating: 5, text: "Ordered the pendant chain and it arrived beautifully packaged. Wearing it every day!" },
  { id: 3, name: "Meera K.",   location: "Bangalore", rating: 5, text: "The bracelet is so delicate and elegant. Perfect gift for my sister's birthday." },
  { id: 4, name: "Shreya T.",  location: "Jaipur",    rating: 4, text: "Great quality silver, very lightweight. Exactly what I was looking for." }
];

// ── Intersection Observer hook ────────────────────────────────────────────────

function useSectionReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("section-visible");
          el.classList.remove("section-hidden");
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

// ── Star rating helper ────────────────────────────────────────────────────────

function StarRating({ rating }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          size={14}
          className={i < rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-zinc-300"}
        />
      ))}
    </div>
  );
}


// ── Main component ────────────────────────────────────────────────────────────

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bestSellers, setBestSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlisted, setWishlisted] = useState({}); // { [product_id]: bool }
  const [wishlistLoading, setWishlistLoading] = useState({}); // per-product loading

  // Section refs for entrance animations
  const bestSellersRef = useSectionReveal();
  const categoriesRef  = useSectionReveal();
  const whyRivoraRef   = useSectionReveal();
  const lifestyleRef   = useSectionReveal();
  const reviewsRef     = useSectionReveal();

  useEffect(() => {
    fetchBestSellers();
  }, []);

  // Once products load, check wishlist status for each (only if logged in)
  useEffect(() => {
    if (!user || bestSellers.length === 0) return;
    bestSellers.forEach(async (p) => {
      try {
        const { data } = await axios.get(`${API}/wishlist/check/${p.product_id}`, { withCredentials: true });
        setWishlisted((prev) => ({ ...prev, [p.product_id]: data.is_wishlisted }));
      } catch { /* silently ignore */ }
    });
  }, [user, bestSellers]);

  const fetchBestSellers = async () => {
    try {
      const { data } = await axios.get(`${API}/products?best_seller=true&limit=6`);
      setBestSellers(data);
    } catch (error) {
      console.error("Failed to fetch best sellers", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleWishlist = async (e, productId) => {
    e.preventDefault(); // don't navigate to product page
    e.stopPropagation();

    if (!user) {
      toast("Please log in to save items", { icon: "🔒" });
      navigate("/login");
      return;
    }

    if (wishlistLoading[productId]) return; // debounce
    setWishlistLoading((prev) => ({ ...prev, [productId]: true }));

    const isWishlisted = wishlisted[productId];
    try {
      if (isWishlisted) {
        await axios.delete(`${API}/wishlist/${productId}`, { withCredentials: true });
        setWishlisted((prev) => ({ ...prev, [productId]: false }));
        toast("Removed from wishlist", { icon: "🤍" });
      } else {
        await axios.post(`${API}/wishlist/${productId}`, {}, { withCredentials: true });
        setWishlisted((prev) => ({ ...prev, [productId]: true }));
        toast.success("Added to wishlist");
      }
    } catch {
      toast.error("Could not update wishlist");
    } finally {
      setWishlistLoading((prev) => ({ ...prev, [productId]: false }));
    }
  };

  return (
    <div className="min-h-screen">

      {/* ── FIX 1: Announcement Bar ──────────────────────────────────────────
          Adds a top strip with free-shipping threshold + Jaipur credibility.
          Update the ₹599 threshold to match your actual free-shipping policy.
      ─────────────────────────────────────────────────────────────────────── */}
      

      <Navbar />

      {/* ── Hero Section ─────────────────────────────────────────────────── */}
      <section
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: "1400/540", minHeight: "480px", maxHeight: "660px" }}
        data-testid="hero-section"
      >
        {/* Full-bleed background image */}
        <img
          src="/heroimage.png"
          alt="Luxury, Crafted in Sterling Silver"
          className="absolute inset-0 w-full h-full object-cover object-right"
          data-testid="hero-image"
        />

        {/* Mobile: full-width gradient so text is always readable */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#f5ede3]/90 via-[#f5ede3]/60 to-[#f5ede3]/10 sm:from-[#f5ede3]/75 sm:via-[#f5ede3]/35 sm:to-transparent" />

        {/* Text content – left-aligned, vertically centred */}
        <div className="relative h-full flex items-center px-6 sm:px-14 lg:px-20" data-testid="hero-content">
          <div className="w-full max-w-[280px] sm:max-w-sm md:max-w-md">

            {/* Headline */}
            <h1
              className="cormorant text-[2rem] sm:text-[2.6rem] lg:text-5xl font-light tracking-tight text-zinc-900 leading-[1.3] mb-3 animate-fade-slide-up"
              data-testid="hero-headline"
            >
              Everyday Luxury<br />in Sterling Silver
            </h1>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-3">
              <span className="block h-px flex-1 max-w-[60px] bg-zinc-400" />
              <span className="text-[#C9A96E] text-xs">◆</span>
            </div>

            {/* Subtext — no forced line break on mobile */}
            <p
              className="text-xs sm:text-sm font-light text-zinc-700 mb-5 leading-relaxed animate-fade-slide-up"
              style={{ animationDelay: "150ms" }}
              data-testid="hero-subtext"
            >
              Discover timeless sterling silver jewellery designed to elevate your everyday style.
            </p>

            {/* CTAs — stack on mobile, row on sm+ */}
            <div
              className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-6 animate-fade-slide-up"
              style={{ animationDelay: "250ms" }}
            >
              <Link
                to="/shop"
                className="inline-block bg-zinc-900 text-white hover:bg-zinc-700 px-6 py-2.5 uppercase tracking-widest text-[11px] font-medium transition-colors rounded-[11px] text-center"
                data-testid="hero-cta-collection"
              >
                Shop Collection
              </Link>
              <Link
                to="/shop?best_seller=true"
                className="inline-block bg-white text-zinc-900 hover:bg-zinc-100 border border-zinc-300 px-6 py-2.5 uppercase tracking-widest text-[11px] font-medium transition-colors rounded-[11px] text-center"
                data-testid="hero-cta-bestsellers"
              >
                Best Sellers
              </Link>
            </div>

            {/* Trust badges — 3-col grid, always fits */}
            <div
              className="grid grid-cols-3 gap-2 animate-fade-slide-up"
              style={{ animationDelay: "320ms" }}
              data-testid="hero-trust-badges"
            >
              <div className="flex flex-col items-center gap-1">
                <Shield size={16} className="text-zinc-600" strokeWidth={1.5} />
                <span className="text-[9px] uppercase tracking-wider text-zinc-500 text-center leading-tight">925 Sterling<br />Silver</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-600">
                  <path d="M6 3h12l4 6-10 13L2 9z" />
                  <path d="M2 9h20M6 3l-4 6M18 3l4 6" />
                </svg>
                <span className="text-[9px] uppercase tracking-wider text-zinc-500 text-center leading-tight">Premium<br />Quality</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-600">
                  <rect x="3" y="8" width="18" height="14" rx="1" />
                  <path d="M21 12H3M12 8v14M12 8a4 4 0 0 0 0-8c-2 0-4 2-4 4s2 4 4 4zm0 0a4 4 0 0 1 0-8c2 0 4 2 4 4s-2 4-4 4z" />
                </svg>
                <span className="text-[9px] uppercase tracking-wider text-zinc-500 text-center leading-tight">Perfect for<br />Gifting</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Best Sellers Section ─────────────────────────────────────────── */}
      <section
        ref={bestSellersRef}
        className="py-12 sm:py-16 bg-white section-hidden"
        data-testid="bestsellers-section"
      >
        <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-zinc-500 mb-4">
              Customer Favorites
            </p>
            <h2 className="cormorant text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
              Best Sellers
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-zinc-900 border-r-transparent"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-10">
              {bestSellers.map((product) => (
                <Link
                  key={product.product_id}
                  to={`/product/${product.product_id}`}
                  className="product-card group"
                  data-testid={`product-${product.product_id}`}
                >
                  {/* Image container — aspect ratio tightened to show more image */}
                  <div className="relative overflow-hidden bg-zinc-100 mb-3 aspect-[3/4] shadow-sm group-hover:shadow-md transition-shadow duration-300">

                    {/* Best Seller badge — champagne brand style */}
                    {product.is_best_seller === true && (
                      <span
                        className="absolute top-2 left-2 text-[10px] px-2.5 py-1 uppercase tracking-widest z-10 font-medium"
                        style={{ background: "#C8A96A", color: "#1a1a1a" }}
                        data-testid="best-seller-badge"
                      >
                        Most Loved
                      </span>
                    )}

                    {/* Low-stock badge */}
                    {product.stock_count !== undefined &&
                      product.stock_count <= 5 &&
                      product.stock_count > 0 && (
                      <span
                        className="absolute top-2 right-2 bg-red-600 text-white text-[10px] px-2.5 py-1 uppercase tracking-widest z-10"
                        data-testid="low-stock-badge"
                      >
                        Only {product.stock_count} left
                      </span>
                    )}

                    {/* Product image — zoom on hover */}
                    <img
                      src={resolveImageUrl(product.image_url)}
                      alt={product.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                    />

                    {/* Quick View overlay */}
                    <div className="absolute inset-0 flex items-end justify-center pb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="bg-white/90 backdrop-blur-sm text-zinc-900 text-[10px] uppercase tracking-widest px-5 py-2 font-medium">
                        Quick View
                      </span>
                    </div>

                    {/* Wishlist heart — fades in on hover, fills when wishlisted */}
                    <button
                      onClick={(e) => toggleWishlist(e, product.product_id)}
                      className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white z-10"
                      aria-label={wishlisted[product.product_id] ? "Remove from wishlist" : "Add to wishlist"}
                    >
                      <Heart
                        size={14}
                        className={wishlisted[product.product_id] ? "text-red-500 fill-red-500" : "text-zinc-700"}
                      />
                    </button>
                  </div>

                  {/* Product name — slightly smaller, more line height */}
                  <h3 className="text-base sm:text-lg font-light cormorant leading-[1.4] mb-1 truncate">{product.name}</h3>

                  {/* Rating — minimal luxury style */}
                  {product.avg_rating && (
                    <p className="text-xs text-zinc-500 mb-1">
                      ⭐ {Number(product.avg_rating).toFixed(1)}
                    </p>
                  )}

                  {/* Price — current price bolder */}
                  <p className="text-sm flex items-center gap-2 flex-wrap">
                    {product.compare_at_price > product.price && (
                      <span className="line-through text-zinc-400 text-xs">
                        ₹{product.compare_at_price.toLocaleString()}
                      </span>
                    )}
                    <span className="font-semibold text-zinc-900">
                      ₹{product.price.toLocaleString()}
                    </span>
                  </p>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link
              to="/shop"
              className="inline-block border border-zinc-900 text-zinc-900 hover:bg-zinc-100 rounded-none px-8 py-4 uppercase tracking-widest text-xs transition-colors"
              data-testid="view-all-button"
            >
              Explore Collection
            </Link>
          </div>
        </div>
      </section>

      {/* ── Categories Section ───────────────────────────────────────────── */}
      <section
        ref={categoriesRef}
        className="py-12 sm:py-16 section-hidden"
        data-testid="categories-section"
      >
        <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-zinc-500 mb-4">Explore</p>
            <h2 className="cormorant text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
              Shop by Category
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {CATEGORIES.map((category, idx) => (
              <Link
                key={idx}
                to={`/shop?category=${encodeURIComponent(category.name)}`}
                className="group block"
                data-testid={`category-${category.name.toLowerCase().replace(" ", "-")}`}
              >
                {/* Image — 4:5 ratio, image scaled up 1.15 to fill padding in PNGs */}
                <div className="relative overflow-hidden bg-zinc-100 mb-4 aspect-[4/5]">
                  <img
                    src={category.image}
                    alt={category.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-center scale-[1.15] transition-transform duration-500 ease-out group-hover:scale-[1.22]"
                  />
                  {/* Dark overlay fades in */}
                  <div className="absolute inset-0 bg-zinc-900/0 group-hover:bg-zinc-900/25 transition-colors duration-400" />
                </div>

                {/* Title — turns gold on hover */}
                <h3 className="text-xl sm:text-2xl font-light cormorant mb-1 transition-colors duration-300 group-hover:text-[#C9A96E]">
                  {category.name}
                </h3>

                {/* "Discover →" — arrow slides right on hover */}
                <p className="text-sm text-zinc-500 flex items-center gap-1 overflow-hidden">
                  <span className="transition-colors duration-300 group-hover:text-zinc-900">Discover</span>
                  <ChevronRight
                    size={15}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Rivora Section ───────────────────────────────────────────── */}
      <section
        ref={whyRivoraRef}
        className="py-16 sm:py-24 bg-[#FAFAFA] section-hidden"
        data-testid="why-rivora-section"
      >
        <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="cormorant text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-8">
              Why Rivora?
            </h2>
            <p className="text-base text-zinc-600 max-w-2xl mx-auto">
              Born in Jaipur — India's jewellery capital — every Rivora piece carries centuries of
              craftsmanship and a modern woman's spirit.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-10">
            {TRUST_POINTS.map(({ icon: Icon, label, desc }, idx) => (
              <div key={idx} className="text-center group cursor-default">
                <Icon
                  size={39}
                  className="text-[#D4AF37] mx-auto mb-6 transition-all duration-300 ease-out group-hover:-translate-y-1 group-hover:text-[#E8C46A]"
                />
                <h3 className="text-xl font-light cormorant mb-4 transition-colors duration-300 group-hover:text-zinc-900">
                  {label}
                </h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* Trust reassurance bar */}
          <div className="mt-20 pt-8 border-t border-zinc-200">
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs uppercase tracking-widest text-zinc-500">
              <span className="flex items-center gap-2">
                <span className="text-[#C9A96E]">✓</span> Free Shipping Across India
              </span>
              <span className="hidden sm:block text-zinc-300">|</span>
              <span className="hidden sm:block text-zinc-300">|</span>
              <span className="flex items-center gap-2">
                <span className="text-[#C9A96E]">✓</span> Secure Payments
              </span>
              <span className="hidden sm:block text-zinc-300">|</span>
              <span className="flex items-center gap-2">
                <span className="text-[#C9A96E]">✓</span> Premium Packaging
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FIX 6: Lifestyle / "Wear It Your Way" Section ───────────────────
          This section was defined but never rendered in the original code.
          Shows the 4 lifestyle images in a square grid to inspire desire.
      ─────────────────────────────────────────────────────────────────────── */}
      <section
        ref={lifestyleRef}
        className="py-12 sm:py-16 bg-white section-hidden"
        data-testid="lifestyle-section"
      >
        <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-zinc-500 mb-4">
              Real Women, Real Style
            </p>
            <h2 className="cormorant text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
              Wear It Your Way
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {LIFESTYLE_IMAGES.map((img, idx) => (
              <div
                key={idx}
                className="relative overflow-hidden bg-zinc-100 aspect-square group"
                data-testid={`lifestyle-image-${idx}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-zinc-900/0 group-hover:bg-zinc-900/20 transition-colors duration-300" />
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/shop"
              className="inline-block border border-zinc-900 text-zinc-900 hover:bg-zinc-100 px-8 py-4 uppercase tracking-widest text-xs transition-colors"
            >
              Explore the Collection
            </Link>
          </div>
        </div>
      </section>

      {/* ── Reviews Section ──────────────────────────────────────────────── */}
      <section
        ref={reviewsRef}
        className="py-12 sm:py-16 bg-[#FAFAFA] section-hidden"
        data-testid="reviews-section"
      >
        <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-zinc-500 mb-4">
              What Our Customers Say
            </p>
            <h2 className="cormorant text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-3">
              Customer Reviews
            </h2>
            {/* Aggregate rating — update count as you grow */}
            <div className="flex items-center justify-center gap-2 mt-2">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }, (_, i) => (
                  <Star key={i} size={16} className="text-[#D4AF37] fill-[#D4AF37]" />
                ))}
              </div>
              <span className="text-sm text-zinc-600">4.9 · Loved by Our First Customers</span>
            </div>
          </div>

          {/* Mobile: horizontal scroll */}
          <div className="flex overflow-x-auto gap-6 pb-4 md:hidden">
            {HOMEPAGE_REVIEWS.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>

          {/* Desktop: 4-column grid */}
          <div className="hidden md:grid md:grid-cols-4 gap-6">
            {HOMEPAGE_REVIEWS.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Newsletter Section ───────────────────────────────────────── */}
      <NewsletterSection />

      <Footer />
    </div>
  );
}

// ── Newsletter sub-component ──────────────────────────────────────────────────

function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || status === "loading") return;
    setStatus("loading");
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="bg-zinc-900 py-16 sm:py-20" data-testid="newsletter-section">
      <div className="px-6 md:px-12 max-w-2xl mx-auto text-center">

        {/* Ornament */}
        <p className="text-[#C9A96E] text-xs tracking-[0.3em] uppercase mb-4">✦ Stay Connected</p>

        <h2 className="cormorant text-3xl sm:text-4xl font-light text-white tracking-tight mb-3">
          Be the First to Know
        </h2>

        <p className="text-sm text-zinc-400 font-light leading-relaxed mb-8 max-w-md mx-auto">
          Discover new collections, exclusive launches and special offers — delivered straight to your inbox.
        </p>

        {status === "success" ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-[#C9A96E]/20 flex items-center justify-center mb-1">
              <span className="text-[#C9A96E] text-lg">✓</span>
            </div>
            <p className="text-white text-sm font-light">You're on the list. Thank you!</p>
            <p className="text-zinc-500 text-xs">We'll be in touch soon.</p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            data-testid="newsletter-form"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Your email address"
              className="flex-1 bg-white/10 border border-white/20 text-white placeholder-zinc-500 px-4 py-3 text-sm focus:outline-none focus:border-[#C9A96E] transition-colors rounded-[11px]"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="bg-[#C9A96E] hover:bg-[#b8955a] text-zinc-900 px-7 py-3 text-xs uppercase tracking-widest font-medium transition-colors disabled:opacity-60 rounded-[11px] whitespace-nowrap"
              data-testid="newsletter-submit"
            >
              {status === "loading" ? (
                <span className="flex items-center gap-2 justify-center">
                  <span className="w-3 h-3 border-2 border-zinc-700/40 border-t-zinc-900 rounded-full animate-spin" />
                  Subscribing…
                </span>
              ) : "Subscribe"}
            </button>
          </form>
        )}

        {status === "error" && (
          <p className="text-red-400 text-xs mt-3">Something went wrong. Please try again.</p>
        )}

        <p className="text-zinc-600 text-[11px] mt-5">
          No spam, ever. Unsubscribe anytime.
        </p>
      </div>
    </section>
  );
}

// ── Review card sub-component ─────────────────────────────────────────────────

function ReviewCard({ review }) {
  return (
    <div
      className="flex-shrink-0 w-72 md:w-auto bg-white rounded-none border border-zinc-100 p-6"
      data-testid={`review-card-${review.id}`}
    >
      <StarRating rating={review.rating} />
      <p className="mt-3 text-sm text-zinc-700 leading-relaxed">{review.text}</p>
      <div className="mt-4">
        <p className="cormorant text-lg font-light">{review.name}</p>
        <p className="text-xs text-zinc-400">{review.location}</p>
      </div>
    </div>
  );
}